import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { SignupFlow } from 'corgi-types';
import type { DisplayProps } from 'styled-system';
import { display, space } from 'styled-system';

import { Link } from 'common/components/Link';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { TextDecorationProps } from 'common/theme/system-utilities';
import { textDecoration } from 'common/theme/system-utilities';
import { getUserFlowQueryParam } from 'common/utils/signup';
import { saveReason } from '../../utils/storage';
import { loginLinkTrackingEvent } from '../../utils/tracking';

interface SignupLinkProps {
  isLoggedIn: boolean;
  reason: string;
}

interface UseClickHandlerOptions extends Pick<SignupLinkProps, 'reason'> {
  label: string;
}

const StyledLink = styled(Link)<SpaceProps & TextDecorationProps & DisplayProps>`
  ${space};
  ${textDecoration};
  ${display};
`;

const useSignupLink = (isLoggedIn: boolean) => {
  const { p, t } = useLocalization();
  const queryParam = getUserFlowQueryParam(SignupFlow.Contact);
  const linkTranslation = isLoggedIn ? 'h24_logout_url' : 'h24_login_page_url';

  return {
    href: `${p.t(linkTranslation)}${queryParam}`,
    label: t(isLoggedIn ? 'h24_logout_link' : 'h24_login_link'),
  };
};

const useClickHandler = ({ reason, label }: UseClickHandlerOptions) =>
  useCallback(() => {
    saveReason(reason);
    loginLinkTrackingEvent(reason, label);
  }, [reason]);

export const SignupLink = ({ isLoggedIn, reason }: SignupLinkProps) => {
  const { href, label } = useSignupLink(isLoggedIn);
  const handleClick = useClickHandler({
    reason,
    label,
  });

  return (
    <StyledLink
      mb="sp_20"
      display="inline-block"
      textDecoration="underline"
      onClick={handleClick}
      href={href}
    >
      {label}
    </StyledLink>
  );
};
